import React, { useState, useEffect, useCallback, useRef } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import axios from "axios"
import { getCurrentUser, setCurrentUser } from "../../helpers/Utils"

const IdleTimer = () => {
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(10)

  const idleTimeoutRef = useRef(null)
  const warningTimeoutRef = useRef(null)
  const countdownIntervalRef = useRef(null)
  const isModalOpenRef = useRef(false)

  // 30 minutes total idle time
  const IDLE_TIME = 1800000 // 30 minutes (30 * 60 * 1000)
  // Show warning 10 seconds before logout
  const WARNING_TIME = 1790000 // Show warning at 29 minutes 50 seconds (30 min - 10 sec)

  const clearAllTimers = useCallback(() => {
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current)
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current)
  }, [])

  const logout = useCallback(() => {
    clearAllTimers()

    // Call logout API
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
          "company_name"
        )}/logout`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${getCurrentUser()?.token || ""}`,
          },
        }
      )
      .catch(() => {
        // Ignore errors on logout
      })
      .finally(() => {
        // Clear user data
        setCurrentUser("")
        localStorage.setItem("DashboardData", "")
        localStorage.setItem("LastUpdated", "")
        localStorage.setItem("NavCounters", "")

        // Hard redirect to login page
        window.location.href = "/login"
      })
  }, [clearAllTimers])

  const startCountdown = useCallback(() => {
    setCountdown(10)
    let count = 10

    countdownIntervalRef.current = setInterval(() => {
      count -= 1
      setCountdown(count)

      if (count <= 0) {
        clearInterval(countdownIntervalRef.current)
        logout()
      }
    }, 1000)
  }, [logout])

  const showWarningModal = useCallback(() => {
    isModalOpenRef.current = true
    setShowWarning(true)
    startCountdown()
  }, [startCountdown])

  const resetTimer = useCallback(() => {
    clearAllTimers()
    isModalOpenRef.current = false
    setShowWarning(false)
    setCountdown(10)

    // Set warning timer (10 seconds before logout)
    warningTimeoutRef.current = setTimeout(() => {
      showWarningModal()
    }, WARNING_TIME)

    // Set logout timer (10 seconds total)
    idleTimeoutRef.current = setTimeout(() => {
      logout()
    }, IDLE_TIME)
  }, [clearAllTimers, showWarningModal, logout])

  const handleStayLoggedIn = useCallback(() => {
    // Clear the countdown interval first
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
    // Hide the warning modal
    setShowWarning(false)
    // Reset the countdown display
    setCountdown(10)

    // Broadcast to other tabs that user is active
    localStorage.setItem("idleTimer_userActive", Date.now().toString())

    // Restart the idle timer
    resetTimer()
  }, [resetTimer])

  useEffect(() => {
    // Only run if user is logged in
    if (!getCurrentUser()) return

    const events = ["mousedown", "keypress", "scroll", "touchstart", "click"]

    const resetTimerHandler = e => {
      // Don't reset timer if the modal is showing and user is clicking on it
      if (showWarning) {
        return
      }
      // Broadcast to other tabs that user is active
      localStorage.setItem("idleTimer_userActive", Date.now().toString())
      resetTimer()
    }

    // Listen for user activity from other tabs
    const handleStorageChange = e => {
      if (e.key === "idleTimer_userActive") {
        // User was active in another tab, reset timer in this tab too
        if (!showWarning) {
          resetTimer()
        } else {
          // If modal is showing, close it and reset timer
          handleStayLoggedIn()
        }
      } else if (e.key === "current_user" && !e.newValue) {
        // User logged out in another tab
        window.location.href = "/login"
      }
    }

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetTimerHandler)
    })

    // Listen for storage events (cross-tab communication)
    window.addEventListener("storage", handleStorageChange)

    // Start the timer initially only on mount
    if (!showWarning) {
      resetTimer()
    }

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimerHandler)
      })
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [showWarning, resetTimer, handleStayLoggedIn])

  return (
    <Modal isOpen={showWarning} backdrop="static" keyboard={false}>
      <ModalHeader>Session Timeout Warning</ModalHeader>
      <ModalBody>
        <div className="text-center">
          <i className="mdi mdi-alert-circle-outline text-warning display-4"></i>
          <h5 className="mt-3">You will be logged out soon!</h5>
          <p className="text-muted">
            You have been idle for too long. You will be automatically logged
            out in:
          </p>
          <div className="mt-3">
            <h1 className="text-danger fw-bold">{countdown}</h1>
            <p className="text-muted">seconds</p>
          </div>
          <p className="text-muted mt-3">
            Click "Stay Logged In" to continue your session.
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleStayLoggedIn}>
          Stay Logged In
        </Button>
        <Button color="secondary" onClick={logout}>
          Logout Now
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default IdleTimer
